import React, { useEffect, useState, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getAllAppointments,
  updateAppointment,
} from "../../actions/appointmentActions";
import MessageModal from "../Pages/MessageModel";
import { getAllPatientProfiles } from "../../actions/patientProfileActions";
import { imgUrl } from "../../actions/types";
import "../../scss/style.scss"; // Your existing styles
import { Link } from "react-router-dom";
import { getScheduleData } from "../../actions/scheduleActions";
import { createAppointment } from "../../actions/appointmentActions";
import { setPatientProfile } from "../../actions/patientProfileActions";
import SelfAppointmentTab from "./SelfAppointmentTab";
import { getBusinessAddons } from "../../actions/businessAddonActions";
import { formatCurrency } from "../utils/currencyHelper";
// --- Date Helper Functions ---
const getStartOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  return getStartOfDay(new Date(d.setDate(diff)));
};

const Appointments = ({
  appointments: allAppointments,
  profiles,
  templateSlots, // Added
  daysOff, // Added
  blockers, // Added
  getAllAppointments,
  getAllPatientProfiles,
  updateAppointment,
  selectedCurrency,
  getScheduleData, // Added
  createAppointment, // Added
  setPatientProfile, // Added
}) => {
  const hospitalCode = localStorage.getItem("hospitalCode");
  const history = useHistory();

  // --- STATE MANAGEMENT ---
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [modeFilter, setModeFilter] = useState("All");
  // --- STATE FOR FILTERS AND SORTING ---
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date_asc");
  const [typeFilter, setTypeFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("all"); // all, today, this_week, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
  const { businessAddons } = useSelector((state) => state.businessAddons);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const doctorMobile = loginData.mobile || "";
  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getAllAppointments(hospitalCode);
        await getAllPatientProfiles();
      } catch (err) {
        setError("Failed to fetch initial data. Please try refreshing.");
      }
    };
    fetchData();
  }, [getAllAppointments, getAllPatientProfiles, sortBy]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getAllAppointments(hospitalCode);
        await getAllPatientProfiles();
        // Fetch schedule for the self-appointment tab
        await getScheduleData({ hospitalID: parseInt(hospitalCode) });
      } catch (err) {
        setError("Failed to fetch initial data.");
      }
    };
    fetchData();
  }, [hospitalCode]);

  // --- DATA PROCESSING & OPTIMIZATION ---
  const hasSelfAppointmentAddon = businessAddons?.some(
    (addon) =>
      addon.addon_name === "Self Appointment" &&
      addon.status?.toLowerCase() === "active",
  );
  const enrichedAppointments = useMemo(() => {
    const patientMap = new Map(profiles.map((p) => [String(p.PatientID), p]));
    return allAppointments
      .filter((apt) => String(apt.hospitalID) === String(hospitalCode))
      .map((apt) => {
        // --- FIX: Correctly parse the date and START time ---
        const startTime = apt.appointmentTime
          ? apt.appointmentTime.split(" - ")[0]
          : "00:00";
        // Create a string that new Date() can reliably parse (e.g., "2025-07-10 12:14 AM")
        const parsableDateTimeString = `${apt.appointmentDate} ${startTime}`;
        const parsedDate = new Date(parsableDateTimeString);

        return {
          ...apt,
          patient: patientMap.get(String(apt.PatientID)) || {},
          // Assign the valid Date object, or null if it's still invalid
          parsedDate: isNaN(parsedDate.getTime()) ? null : parsedDate,
        };
      });
  }, [allAppointments, profiles, hospitalCode]);

  const displayedAppointments = useMemo(() => {
    // Filter out any appointments that failed to parse a date before sorting/filtering
    let processedAppointments = enrichedAppointments.filter(
      (apt) => apt.parsedDate,
    );

    // 1. Apply Status Filter
    if (statusFilter !== "All") {
      processedAppointments = processedAppointments.filter(
        (apt) => apt.status === statusFilter,
      );
    }

    // 2. Apply Type Filter
    if (typeFilter !== "All") {
      processedAppointments = processedAppointments.filter(
        (apt) => apt.type === typeFilter,
      );
    }
    // 2.5 Apply Mode Filter (Online/Offline Slots)
    if (modeFilter !== "All") {
      processedAppointments = processedAppointments.filter(
        (apt) => apt.AppoinmentFor === modeFilter,
      );
    }
    // 3. Apply Date Filter
    const now = new Date();
    switch (dateFilter) {
      case "today":
        const todayStart = getStartOfDay(now);
        const todayEnd = getEndOfDay(now);
        processedAppointments = processedAppointments.filter(
          (apt) => apt.parsedDate >= todayStart && apt.parsedDate <= todayEnd,
        );
        break;
      case "this_week":
        const weekStart = getStartOfWeek(now);
        const weekEnd = getEndOfDay(
          new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000),
        );
        processedAppointments = processedAppointments.filter(
          (apt) => apt.parsedDate >= weekStart && apt.parsedDate <= weekEnd,
        );
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const start = getStartOfDay(customStartDate);
          const end = getEndOfDay(customEndDate);
          processedAppointments = processedAppointments.filter(
            (apt) => apt.parsedDate >= start && apt.parsedDate <= end,
          );
        }
        break;
      default:
        // 'all' case, do nothing
        break;
    }

    // 4. Apply Search Filter
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      processedAppointments = processedAppointments.filter(({ patient }) =>
        `${patient.firstName} ${patient.lastName} ${patient.PatientID}`
          .toLowerCase()
          .includes(lowerCaseSearchTerm),
      );
    }

    // 5. Apply Client-side Sorting
    processedAppointments.sort((a, b) => {
      switch (sortBy) {
        case "date_desc":
          return b.parsedDate - a.parsedDate;
        case "id_asc":
          return a.appointmentId - b.appointmentId;
        case "id_desc":
          return b.appointmentId - a.appointmentId;
        case "date_asc":
        default:
          return a.parsedDate - b.parsedDate;
      }
    });

    return processedAppointments;
  }, [
    enrichedAppointments,
    statusFilter,
    typeFilter,
    modeFilter,
    dateFilter,
    customStartDate,
    customEndDate,
    searchTerm,
    sortBy,
  ]);
  // --- PAGINATION LOGIC ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayedAppointments.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(displayedAppointments.length / itemsPerPage);

  // Reset to page 1 whenever filters or items per page change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    typeFilter,
    modeFilter,
    dateFilter,
    itemsPerPage,
  ]);
  // --- HANDLER FUNCTIONS ---
  const handleUpdate = async (appointmentId, status) => {
    try {
      setUpdatingId(appointmentId);
      await updateAppointment({ appointmentId, status });
      await getAllAppointments();
    } catch (err) {
      setError(`Failed to update appointment #${appointmentId}.`);
    } finally {
      setUpdatingId(null);
    }
  };
  const handleWhatsAppMessage = (apt) => {
    const { patient, appointmentDate, appointmentTime, patient_name } = apt;

    const fullAddress = `${patient.address || "N/A"}, ${patient.city || ""}, ${patient.state || ""} - ${patient.postalCode || ""}`;

    const message =
      `*Home Visit Appointment Details*%0A%0A` +
      `*Patient Name:* ${patient_name || patient.firstName + " " + patient.lastName}%0A` +
      `*Date:* ${appointmentDate}%0A` +
      `*Time:* ${appointmentTime}%0A` +
      `*Address:* ${fullAddress}%0A%0A`;

    const whatsappUrl = `https://wa.me/${doctorMobile}?text=${message}`;

    window.open(whatsappUrl, "_blank");
  };
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Confirmed":
      case "Confirm":
        return "confirm-label";
      case "Canceled":
      case "Cancel":
        return "cancel-label";
      case "Pending":
        return "pending-label";
      default:
        return "cancel-label";
    }
  };
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  const renderItemsPerPageSelector = () => (
    <div className="dataTables_length">
      <label>
        Items:
        <select
          className="form-control form-control-sm ml-2"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          style={{ width: "70px", display: "inline-block" }}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </label>
    </div>
  );

const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; 
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
        <div className="dataTables_paginate paging_simple_numbers">
            <ul className="pagination">
                <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    <a href="#!" onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }} className="page-link">Previous</a>
                </li>
                
                {startPage > 1 && (
                    <li className="paginate_button page-item">
                        <a href="#!" onClick={(e) => { e.preventDefault(); handlePageChange(1); }} className="page-link">1</a>
                    </li>
                )}

                {pageNumbers.map(number => (
                    <li key={number} className={`paginate_button page-item ${currentPage === number ? 'active' : ''}`}>
                        <a href="#!" onClick={(e) => { e.preventDefault(); handlePageChange(number); }} className="page-link">{number}</a>
                    </li>
                ))}

                <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <a href="#!" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }} className="page-link">Next</a>
                </li>
            </ul>
        </div>
    );
};
  //   return (
  //     <div className="appointments">
  //       <div className="page-header filter-bar rounded ">
  //                 <div className="row align-items-center">
  //                     <div className="col">
  //                         <h4 className="page-title">Appointments</h4>
  //                         <ul className="breadcrumb">
  //                             <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
  //                             <li className="breadcrumb-item active">Appointments</li>
  //                         </ul>
  //                     </div>
  //                 </div>
  //             </div>
  //              <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded mt-4">
  //       <li className="nav-item">
  //         <button
  //           className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
  //           onClick={() => setActiveTab('list')}
  //         >
  //           All Appointments ({displayedAppointments.length})
  //         </button>
  //       </li>
  //       <li className="nav-item">
  //         <button
  //           className={`nav-link ${activeTab === 'self' ? 'active' : ''}`}
  //           onClick={() => setActiveTab('self')}
  //         >
  //           Self Appointment
  //         </button>
  //       </li>
  //     </ul>
  //       <div className="card shadow-sm mb-4">
  //         <div className="card-header bg-light border-bottom">
  //           <h4 className="card-title mb-0">Filter & Sort Appointments</h4>
  //         </div>
  //         <div className="card-body">
  //           <div className="row">
  //             <div className="col-md-3 form-group">
  //               <label>Search Patient</label>
  //               <input
  //                 type="text"
  //                 className="form-control"
  //                 placeholder="Name or ID..."
  //                 value={searchTerm}
  //                 onChange={(e) => setSearchTerm(e.target.value)}
  //               />
  //             </div>
  //             <div className="col-md-3 form-group">
  //               <label>Status</label>
  //               <select
  //                 className="form-control"
  //                 value={statusFilter}
  //                 onChange={(e) => setStatusFilter(e.target.value)}
  //               >
  //                 <option value="All">All Statuses</option>
  //                 <option value="Pending">Pending</option>
  //                 <option value="Confirmed">Confirmed</option>
  //                 <option value="Canceled">Canceled</option>
  //               </select>
  //             </div>
  //             <div className="col-md-3 form-group">
  //               <label>Appointment Type</label>
  //               <select
  //                 className="form-control"
  //                 value={typeFilter}
  //                 onChange={(e) => setTypeFilter(e.target.value)}
  //               >
  //                 <option value="All">All Types</option>
  //                 <option value="direct">Direct</option>
  //                 <option value="audio/video">Video/Audio Call</option>
  //               </select>
  //             </div>
  //             <div className="col-md-3 form-group">
  //               <label>Sort By</label>
  //               <select
  //                 className="form-control"
  //                 value={sortBy}
  //                 onChange={(e) => setSortBy(e.target.value)}
  //               >
  //                 <option value="date_asc">Date (Soonest First)</option>
  //                 <option value="date_desc">Date (Latest First)</option>
  //                 <option value="id_asc">Appointment ID (Asc)</option>
  //                 <option value="id_desc">Appointment ID (Desc)</option>
  //               </select>
  //             </div>
  //           </div>
  //           <div className="row align-items-end">
  //             <div className="col-md-12">
  //               <label>Date Range</label>
  //               <div className="d-flex align-items-center flex-wrap">
  //                 <div className="form-check form-check-inline">
  //                   <input
  //                     className="form-check-input"
  //                     type="radio"
  //                     name="dateFilter"
  //                     id="dateAll"
  //                     value="all"
  //                     checked={dateFilter === "all"}
  //                     onChange={(e) => setDateFilter(e.target.value)}
  //                   />
  //                   <label className="form-check-label" htmlFor="dateAll">
  //                     All
  //                   </label>
  //                 </div>
  //                 <div className="form-check form-check-inline">
  //                   <input
  //                     className="form-check-input"
  //                     type="radio"
  //                     name="dateFilter"
  //                     id="dateToday"
  //                     value="today"
  //                     checked={dateFilter === "today"}
  //                     onChange={(e) => setDateFilter(e.target.value)}
  //                   />
  //                   <label className="form-check-label" htmlFor="dateToday">
  //                     Today
  //                   </label>
  //                 </div>
  //                 <div className="form-check form-check-inline">
  //                   <input
  //                     className="form-check-input"
  //                     type="radio"
  //                     name="dateFilter"
  //                     id="dateWeek"
  //                     value="this_week"
  //                     checked={dateFilter === "this_week"}
  //                     onChange={(e) => setDateFilter(e.target.value)}
  //                   />
  //                   <label className="form-check-label" htmlFor="dateWeek">
  //                     This Week
  //                   </label>
  //                 </div>
  //                 <div className="form-check form-check-inline">
  //                   <input
  //                     className="form-check-input"
  //                     type="radio"
  //                     name="dateFilter"
  //                     id="dateCustom"
  //                     value="custom"
  //                     checked={dateFilter === "custom"}
  //                     onChange={(e) => setDateFilter(e.target.value)}
  //                   />
  //                   <label className="form-check-label" htmlFor="dateCustom">
  //                     Custom
  //                   </label>
  //                 </div>
  //                 {dateFilter === "custom" && (
  //                   <div className="d-flex align-items-center gap-2 mt-2 mt-md-0 ml-md-2">
  //                     <input
  //                       type="date"
  //                       className="form-control form-control-sm"
  //                       value={customStartDate}
  //                       onChange={(e) => setCustomStartDate(e.target.value)}
  //                     />
  //                     <span>-</span>
  //                     <input
  //                       type="date"
  //                       className="form-control form-control-sm"
  //                       value={customEndDate}
  //                       onChange={(e) => setCustomEndDate(e.target.value)}
  //                     />
  //                   </div>
  //                 )}
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  // <div className='card'>
  // <div className='card-body'>
  //       {error && (
  //         <div className="alert alert-danger text-center" role="alert">
  //           {error}
  //         </div>
  //       )}

  //       <div className="row">
  //         {enrichedAppointments.length === 0 ? (
  //           <div className="col-12">
  //             <p className="text-center text-muted mt-5">
  //               No appointments found for this hospital.
  //             </p>
  //           </div>
  //         ) : displayedAppointments.length === 0 ? (
  //           <div className="col-12">
  //             <p className="text-center text-muted mt-5">
  //               No appointments match your current filters.
  //             </p>
  //           </div>
  //         ) : (
  //           displayedAppointments.map((apt) => {
  //             const { patient } = apt;
  //             const patientImg = patient.uploadPhoto
  //               ? `${imgUrl}${patient.uploadPhoto}`
  //               : "/assets/img/patients/patient.jpg";
  //             const isUpdating = updatingId === apt.appointmentId;
  //             return (
  //               <div
  //                 className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4"
  //                 key={apt.appointmentId}
  //               >
  //                 <div className="card appointment-card h-100 shadow-sm border-0 d-flex flex-column">
  //                   {/* Card Header */}
  //                   <div className="card-header bg-white p-3">
  //                     <div
  //                       className="d-flex align-items-center"
  //                       onClick={() =>
  //                         (window.location.href = `#/patient-profile/${patient.PatientID}`)
  //                       }
  //                       style={{ cursor: "pointer" }}
  //                       title="View Patient Profile"
  //                     >
  //                       <img
  //                         src={patientImg}
  //                         alt="Patient"
  //                         className="avatar-sm rounded-circle me-3"
  //                       />
  //                       <div className="flex-grow-1 min-width-0">
  //                         <h6 className="mb-0 fw-bold text-truncate">
  //                           {patient.firstName} {patient.lastName}
  //                           {patient.firstName + " " + patient.lastName !==
  //                             apt.patient_name && (
  //                             <span className="text-muted d-block small fw-normal">
  //                               (booked for {apt.patient_name})
  //                             </span>
  //                           )}
  //                         </h6>
  //                         <small className="text-muted">
  //                           ID: {patient.PatientID || "N/A"}
  //                         </small>
  //                       </div>
  //                     </div>
  //                   </div>

  //                   {/* Card Body */}
  //                   <div className="card-body p-3 flex-grow-0 py-1">
  //                     <ul className="list-unstyled mb-0">
  //                       <li className="d-flex align-items-center mb-2">
  //                         <i className="fas fa-hashtag text-primary fa-fw me-2" />
  //                         <span className="me-2">
  //                           <strong>Status:</strong>
  //                         </span>
  //                         <span className={`badge ${getStatusBadge(apt.status)}`}>
  //                           {apt.status}
  //                         </span>
  //                       </li>
  //                       <li className="d-flex align-items-center mb-2">
  //                         <i className="fas fa-hashtag text-primary fa-fw me-2" />
  //                         <span>
  //                           <strong>Appt ID:</strong> {apt.appointmentId}
  //                         </span>
  //                       </li>
  //                       <li className="d-flex align-items-center mb-2">
  //                         <i className="far fa-calendar-alt text-success fa-fw me-2" />
  //                         <span>
  //                           <strong>Date:</strong> {apt.appointmentDate}
  //                         </span>
  //                       </li>
  //                       <li className="d-flex align-items-center mb-2">
  //                         <i className="far fa-clock text-info fa-fw me-2" />
  //                         <span>
  //                           <strong>Time:</strong> {apt.appointmentTime}
  //                         </span>
  //                       </li>
  //                       <li className="d-flex align-items-center">
  //                         <i className="fas fa-stethoscope text-warning fa-fw me-2" />
  //                         <span>
  //                           <strong>Type:</strong>{" "}
  //                           <span className="text-capitalize">
  //                             {apt.type || "N/A"}
  //                           </span>
  //                         </span>
  //                       </li>
  //                     </ul>
  //                     <ul className="mt-1">

  //                         <div className="mt-0 d-flex justify-content-end gap-2 pt-1 pr-2">
  //     {/* View Button */}
  //     <button
  //         className="i-btn-view mr-1"
  //         onClick={() => handleViewDetails(apt)}
  //         data-toggle="modal"
  //         data-target="#appt_details"
  //       title=" View Appointment Details"
  //     >
  //         {/* Button content empty, relies on CSS ::before */}
  //     </button>
  //     {/* Confirm Button */}
  //     <button
  //          type="button"
  //         className="i-btn-confirm mr-1"
  //         onClick={() =>
  //             handleUpdate(apt.appointmentId, "Confirmed")
  //         }
  //         title="Confirm Appointment"
  //     >
  //         {isUpdating && apt.status !== "Confirmed" ? (
  //             <span className="spinner-border spinner-border-sm"></span>
  //         ) : (
  //             // Changed from <i/> to null for cleaner rendering
  //             null
  //         )}
  //     </button>
  //     {/* Cancel Button */}
  //     <button
  //          type="button"
  //         className="i-btn-cancel mr-1"
  //         onClick={() =>
  //             handleUpdate(apt.appointmentId, "Canceled")
  //         }
  //         title="Cancel Appointment"
  //     >
  //         {isUpdating && apt.status !== "Canceled" ? (
  //             <span className="spinner-border spinner-border-sm"></span>
  //         ) : (
  //             // Changed from <i/> to null for cleaner rendering
  //             null
  //         )}
  //     </button>
  // </div>

  //                     </ul>
  //                   </div>

  //                   {/* Card Footer - Improved Button Layout */}

  //                 </div>
  //               </div>
  //             );
  //           })
  //         )}
  //       </div>

  //       {/* Modal remains unchanged */}
  //       {/* <div className="modal fade custom-modal" id="appt_details">
  //         <div className="modal-dialog modal-dialog-centered">
  //           <div className="modal-content">
  //             <div className="modal-header bg-info text-white">
  //               <h5 className="modal-title">Appointment Details</h5>
  //               <button
  //                 type="button"
  //                 className="close"
  //                 data-dismiss="modal"
  //                 aria-label="Close"
  //               >
  //                 <span aria-hidden="true">×</span>
  //               </button>
  //             </div>
  //             <div className="modal-body">
  //               {selectedAppointment ? (
  //                 <ul className="info-details">
  //                   <li>
  //                     <div className="details-header">
  //                       <div className="row">
  //                         <div className="col-md-6">
  //                           <span className="title">
  //                             #{selectedAppointment.appointmentId}
  //                           </span>
  //                           <span className="text">
  //                             {selectedAppointment.appointmentDate}{" "}
  //                             {selectedAppointment.appointmentTime}
  //                           </span>
  //                         </div>
  //                         <div className="col-md-6 text-right">
  //                            <span
  //                                             className={`badge badge-pill p-2 ${
  //                                                 selectedAppointment.status === "Confirmed" ? "badge-success" :
  //                                                 selectedAppointment.status === "Canceled" ? "badge-danger" : "badge-warning"
  //                                             }`}

  //                                         >
  //                                             {selectedAppointment.status}
  //                                         </span>

  //                         </div>
  //                       </div>
  //                     </div>
  //                   </li>
  //                   <li>
  //                     <span className="title">Appointment Booked By:</span>{" "}
  //                     <span className="text">
  //                       {selectedAppointment.patient?.firstName ||
  //                         selectedAppointment.patient?.lastName}
  //                     </span>
  //                   </li>

  //                   <li>
  //                     <span className="title">Patient Name:</span>{" "}
  //                     <span className="text">
  //                       {selectedAppointment.patient_name ||
  //                         selectedAppointment.patient?.firstName}
  //                     </span>
  //                   </li>
  //                   <li>
  //                     <span className="title">Email:</span>{" "}
  //                     <span className="text">
  //                       {selectedAppointment.patient?.email || "N/A"}
  //                     </span>
  //                   </li>
  //                   <li>
  //                     <span className="title">Phone:</span>{" "}
  //                     <span className="text">
  //                       {selectedAppointment.patient?.phoneNumber || "N/A"}
  //                     </span>
  //                   </li>
  //                   <li>
  //                     <span className="title">Type:</span>{" "}
  //                     <span className="text">{selectedAppointment.type}</span>
  //                   </li>
  //                   <li>
  //                     <span className="title">Paid Amount:</span>{" "}
  //                     <span className="text">
  //                       {selectedAppointment.paidAmount || "N/A"}
  //                     </span>
  //                   </li>
  //                 </ul>
  //               ) : (
  //                 <p>Loading...</p>
  //               )}
  //             </div>
  //           </div>
  //         </div>
  //       </div> */}
  //       {selectedAppointment && (
  //   <div
  //     className="modal show d-block"
  //     style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
  //   >
  //     <div className="modal-dialog modal-dialog-centered">
  //       <div className="modal-content">
  //         {/* Updated Header: Matches Invoice Report style */}
  //         <div className="modal-header bg-info text-white no-print">
  //           <h5 className="modal-title">Appointment Details</h5>
  //           <button
  //             type="button"
  //             className="btn-close btn-close-white"
  //             onClick={() => setSelectedAppointment(null)} // This fixes the closing issue
  //             aria-label="Close"
  //           ></button>
  //         </div>

  //         <div className="modal-body">
  //           <ul className="info-details">
  //             <li>
  //               <div className="details-header">
  //                 <div className="row align-items-center">
  //                   <div className="col-md-6">
  //                     <span className="title">
  //                       #{selectedAppointment.appointmentId}
  //                     </span>
  //                     <span className="text">
  //                       {selectedAppointment.appointmentDate}{" "}
  //                       {selectedAppointment.appointmentTime}
  //                     </span>
  //                   </div>
  //                   <div className="col-md-6 text-right text-md-end">
  //                     {/* Integrated your requested Status Badge Logic */}
  //                     <span
  //                       className={`badge badge-pill p-2 ${
  //                         selectedAppointment.status === "Confirmed" ? "badge-success" :
  //                         selectedAppointment.status === "Canceled" ? "badge-danger" : "badge-warning"
  //                       }`}
  //                     >
  //                       {selectedAppointment.status}
  //                     </span>
  //                   </div>
  //                 </div>
  //               </div>
  //             </li>

  //             <li>
  //               <span className="title">Appointment Booked By:</span>{" "}
  //               <span className="text">
  //                 {selectedAppointment.patient?.firstName || selectedAppointment.patient?.lastName}
  //               </span>
  //             </li>

  //             <li>
  //               <span className="title">Patient Name:</span>{" "}
  //               <span className="text">
  //                 {selectedAppointment.patient_name || selectedAppointment.patient?.firstName}
  //               </span>
  //             </li>

  //             <li>
  //               <span className="title">Email:</span>{" "}
  //               <span className="text">
  //                 {selectedAppointment.patient?.email || "N/A"}
  //               </span>
  //             </li>

  //             <li>
  //               <span className="title">Phone:</span>{" "}
  //               <span className="text">
  //                 {selectedAppointment.patient?.phoneNumber || "N/A"}
  //               </span>
  //             </li>

  //             <li>
  //               <span className="title">Type:</span>{" "}
  //               <span className="text">{selectedAppointment.type}</span>
  //             </li>

  //             <li>
  //               <span className="title">Paid Amount:</span>{" "}
  //               <span className="text">
  //                 {selectedAppointment.paidAmount || "N/A"}
  //               </span>
  //             </li>
  //           </ul>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // )}
  //     </div>
  // </div>
  // </div>
  //   );
  return (
    <div className="appointments">
      <style>{`
    .pagination-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        margin-top: 25px;
        padding: 15px 0;
        border-top: 1px solid #dee2e6;
    }
    .pagination-controls-wrapper {
        display: flex;
        align-items: center;
    }
    .pagination {
        margin: 0;
    }
    .pagination .page-item.active .page-link {
        background-color: #0084d9;
        border-color: #0084d9;
        color: white;
    }
    .pagination .page-link {
        color: #0084d9;
        padding: 0.5rem 0.75rem;
    }
    .dataTables_info {
        font-size: 0.9rem;
        color: #6c757d;
        margin-right: 20px;
    }
    @media (max-width: 768px) {
        .pagination-wrapper {
            flex-direction: column;
            gap: 15px;
            text-align: center;
        }
        .pagination-controls-wrapper {
            flex-direction: column;
            gap: 10px;
        }
    }
`}</style>
      {/* Page Header */}
      <div className="page-header filter-bar rounded">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="page-title">Appointments</h4>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/doctor/dashboard">Doctor Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Appointments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      {/* <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded mt-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            All Appointments ({displayedAppointments.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "self" ? "active" : ""}`}
            onClick={() => setActiveTab("self")}
          >
            Add Appointment
          </button>
        </li>
      </ul> */}
      <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded mt-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            All Appointments ({displayedAppointments.length})
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "self" ? "active" : ""}`}
            onClick={() => {
              if (hasSelfAppointmentAddon) {
                setActiveTab("self");
              } else {
                setShowAddonModal(true);
              }
            }}
            style={!hasSelfAppointmentAddon ? { cursor: "pointer" } : {}}
          >
            {!hasSelfAppointmentAddon && (
              <i
                className="fas fa-crown me-2 text-warning"
                style={{ fontSize: "14px" }}
              ></i>
            )}
            Add Appointment
          </button>
        </li>
      </ul>
      <div className="tab-content mt-3">
        {/* ALL APPOINTMENTS TAB */}
        {activeTab === "list" && (
          <>
            {/* Filter & Sort Card */}
            {/* <div className="card shadow-sm mb-4">
              <div className="card-header bg-light border-bottom">
                <h4 className="card-title mb-0">Filter & Sort Appointments</h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 form-group">
                    <label>Search Patient</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3 form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All Statuses</option>
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Canceled">Canceled</option>
                    </select>
                  </div>
                  <div className="col-md-3 form-group">
                    <label>Appointment Type</label>
                    <select
                      className="form-control"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                    >
                      <option value="All">All Types</option>
                      <option value="direct">Direct</option>
                      <option value="video">Video</option>
                      <option value="home">Home </option>
                    </select>
                  </div>
                  <div className="col-md-2 form-group">
                    <label>Mode</label>
                    <select
                      className="form-control"
                      value={modeFilter}
                      onChange={(e) => setModeFilter(e.target.value)}
                    >
                      <option value="All">All Slots</option>
                      <option value="Online">Online Slots</option>
                      <option value="Offline">Offline Slots</option>
                    </select>
                  </div>
                  <div className="col-md-3 form-group">
                    <label>Sort By</label>
                    <select
                      className="form-control"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date_asc">Date (Soonest First)</option>
                      <option value="date_desc">Date (Latest First)</option>
                      <option value="id_asc">Appointment ID (Asc)</option>
                      <option value="id_desc">Appointment ID (Desc)</option>
                    </select>
                  </div>
                </div>
                <div className="row align-items-end">
                  <div className="col-md-12">
                    <label>Date Range</label>
                    <div className="d-flex align-items-center flex-wrap">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateAll"
                          value="all"
                          checked={dateFilter === "all"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateAll">
                          All
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateToday"
                          value="today"
                          checked={dateFilter === "today"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateToday">
                          Today
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateWeek"
                          value="this_week"
                          checked={dateFilter === "this_week"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateWeek">
                          This Week
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateCustom"
                          value="custom"
                          checked={dateFilter === "custom"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="dateCustom"
                        >
                          Custom
                        </label>
                      </div>
                      {dateFilter === "custom" && (
                        <div className="d-flex align-items-center gap-2 mt-2 mt-md-0 ml-md-2">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                          />
                          <span>-</span>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
            {/* Filter & Sort Card */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-light border-bottom">
                <h4 className="card-title mb-0">Filter & Sort Appointments</h4>
              </div>
              <div className="card-body">
                {/* First Row: 5 Options */}
                <div className="row">
                  {/* 1. Search (Width: 3/12) */}
                  <div className="col-md-3 form-group">
                    <label>Search Patient</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* 2. Status (Width: 2/12) */}
                  <div className="col-md-2 form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All Statuses</option>
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Canceled">Canceled</option>
                    </select>
                  </div>

                  {/* 3. Type (Width: 2/12) */}
                  <div className="col-md-2 form-group">
                    <label>Appointment Type</label>
                    <select
                      className="form-control"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                    >
                      <option value="All">All Types</option>
                      <option value="direct">Direct</option>
                      <option value="video">Video</option>
                      <option value="home">Home </option>
                    </select>
                  </div>

                  {/* 4. Mode (Width: 2/12) */}
                  <div className="col-md-2 form-group">
                    <label>Mode</label>
                    <select
                      className="form-control"
                      value={modeFilter}
                      onChange={(e) => setModeFilter(e.target.value)}
                    >
                      <option value="All">All Slots</option>
                      <option value="Online">Online Slots</option>
                      <option value="Offline">Offline Slots</option>
                    </select>
                  </div>

                  {/* 5. Sort By (Width: 3/12) */}
                  <div className="col-md-3 form-group">
                    <label>Sort By</label>
                    <select
                      className="form-control"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date_asc">Date (Soonest First)</option>
                      <option value="date_desc">Date (Latest First)</option>
                      <option value="id_asc">Appointment ID (Asc)</option>
                      <option value="id_desc">Appointment ID (Desc)</option>
                    </select>
                  </div>
                </div>

                {/* Second Row: Date Range */}
                <div className="row mt-2">
                  <div className="col-md-12">
                    <label className="d-block">Date Range</label>
                    <div
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: "15px" }}
                    >
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateAll"
                          value="all"
                          checked={dateFilter === "all"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateAll">
                          All
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateToday"
                          value="today"
                          checked={dateFilter === "today"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateToday">
                          Today
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateWeek"
                          value="this_week"
                          checked={dateFilter === "this_week"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="dateWeek">
                          This Week
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateFilter"
                          id="dateCustom"
                          value="custom"
                          checked={dateFilter === "custom"}
                          onChange={(e) => setDateFilter(e.target.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="dateCustom"
                        >
                          Custom
                        </label>
                      </div>

                      {/* Custom Date Inputs - Shown only when "Custom" is selected */}
                      {dateFilter === "custom" && (
                        <div className="d-flex align-items-center gap-2 animate__animated animate__fadeIn">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={customStartDate}
                            style={{ width: "150px" }}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                          />
                          <span className="mx-1">to</span>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={customEndDate}
                            style={{ width: "150px" }}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Appointment Grid List */}
            <div className="card">
              <div className="card-body">
                {error && (
                  <div className="alert alert-danger text-center" role="alert">
                    {error}
                  </div>
                )}

                <div className="row">
                  {enrichedAppointments.length === 0 ? (
                    <div className="col-12">
                      <p className="text-center text-muted mt-5">
                        No appointments found for this hospital.
                      </p>
                    </div>
                  ) : displayedAppointments.length === 0 ? (
                    <div className="col-12">
                      <p className="text-center text-muted mt-5">
                        No appointments match your current filters.
                      </p>
                    </div>
                  ) : (
                    currentItems.map((apt) => {
                      const { patient } = apt;
                      const patientImg = patient.uploadPhoto
                        ? `${imgUrl}${patient.uploadPhoto}`
                        : "/assets/img/patients/patient.jpg";
                      const isUpdating = updatingId === apt.appointmentId;
                      return (
                        <div
                          className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4"
                          key={apt.appointmentId}
                        >
                          <div className="card appointment-card h-100 shadow-sm border-0 d-flex flex-column">
                            <div className="card-header bg-white p-3">
                              <div
                                className="d-flex align-items-center"
                                onClick={() =>
                                  (window.location.href = `#/patient-profile/${patient.PatientID}`)
                                }
                                style={{ cursor: "pointer" }}
                                title="View Patient Profile"
                              >
                                <img
                                  src={patientImg}
                                  alt="Patient"
                                  className="avatar-sm rounded-circle me-3"
                                />
                                <div className="flex-grow-1 min-width-0">
                                  <h6 className="mb-0 fw-bold text-truncate">
                                    {patient.firstName} {patient.lastName}
                                    {patient.firstName +
                                      " " +
                                      patient.lastName !==
                                      apt.patient_name && (
                                      <span className="text-muted d-block small fw-normal">
                                        (booked for {apt.patient_name})
                                      </span>
                                    )}
                                  </h6>
                                  <small className="text-muted">
                                    ID: {patient.PatientID || "N/A"}
                                  </small>
                                </div>
                              </div>
                            </div>

                            <div className="card-body p-3 flex-grow-0 py-1">
                              <ul className="list-unstyled mb-0">
                                <li className="d-flex align-items-center mb-2">
                                  <i className="fas fa-hashtag text-primary fa-fw me-2" />
                                  <span className="me-2">
                                    <strong>Status:</strong>
                                  </span>
                                  <span
                                    className={`badge ${getStatusBadge(
                                      apt.status,
                                    )}`}
                                  >
                                    {apt.status}
                                  </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                  <i className="fas fa-hashtag text-primary fa-fw me-2" />
                                  <span>
                                    <strong>Appt ID:</strong>{" "}
                                    {apt.appointmentId}
                                  </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                  <i className="far fa-calendar-alt text-success fa-fw me-2" />
                                  <span>
                                    <strong>Date:</strong> {apt.appointmentDate}
                                  </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                  <i className="far fa-clock text-info fa-fw me-2" />
                                  <span>
                                    <strong>Time:</strong> {apt.appointmentTime}
                                  </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                  <i className="fas fa-stethoscope text-warning fa-fw me-2" />
                                  <span>
                                    <strong>Type:</strong>{" "}
                                    <span className="text-capitalize">
                                      {apt.type || "N/A"}
                                    </span>
                                  </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                  <i className="fas fa-laptop-medical text-info fa-fw me-2" />
                                  <span>
                                    <strong>Mode:</strong>{" "}
                                    {apt.AppoinmentFor || "Offline"}
                                  </span>
                                </li>
                              </ul>
                              <div className="mt-2 d-flex justify-content-end gap-2 pt-1 pr-2">
                                {/* WhatsApp Button - Square UI with Center Icon */}
                                {apt.type?.toLowerCase() === "home" && (
                                  <button
                                    type="button"
                                    className="i-btn-whatsapp" // Humne upar wali class use ki
                                    onClick={() => handleWhatsAppMessage(apt)}
                                    title="Send Address to WhatsApp"
                                  >
                                    {/* WhatsApp Icon perfectly centered via CSS flex */}
                                    <i className="fab fa-whatsapp"></i>
                                  </button>
                                )}
                                <button
                                  className="i-btn-view mr-1"
                                  onClick={() => handleViewDetails(apt)}
                                  title="View Appointment Details"
                                />
                                <button
                                  type="button"
                                  className="i-btn-confirm mr-1"
                                  onClick={() =>
                                    handleUpdate(apt.appointmentId, "Confirmed")
                                  }
                                  title="Confirm Appointment"
                                  disabled={isUpdating}
                                >
                                  {isUpdating && apt.status !== "Confirmed" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : null}
                                </button>
                                <button
                                  type="button"
                                  className="i-btn-cancel mr-1"
                                  onClick={() =>
                                    handleUpdate(apt.appointmentId, "Canceled")
                                  }
                                  title="Cancel Appointment"
                                  disabled={isUpdating}
                                >
                                  {isUpdating && apt.status !== "Canceled" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : null}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
               {displayedAppointments.length > 0 && (
        <div className="pagination-wrapper">
            {/* Left Side: Items Selector */}
            <div className="items-per-page-wrapper">
                {renderItemsPerPageSelector()}
            </div>

            {/* Right Side: Showing text + Buttons */}
            <div className="pagination-controls-wrapper">
                <div className="dataTables_info">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, displayedAppointments.length)} of {displayedAppointments.length} entries
                </div>
                {renderPagination()}
            </div>
        </div>
    )}
              </div>
            </div>
          </>
        )}

        {/* SELF APPOINTMENT TAB */}
        {activeTab === "self" && (
          <div className="card">
            <div className="card-body">
              <SelfAppointmentTab
                hospitalCode={parseInt(hospitalCode)}
                templateSlots={templateSlots}
                daysOff={daysOff}
                blockers={blockers}
                bookedAppointments={allAppointments}
                profiles={profiles}
                setPatientProfile={setPatientProfile}
                createAppointment={createAppointment}
                getScheduleData={getScheduleData}
                getAllAppointments={getAllAppointments}
                isCreatingAppointment={!!updatingId}
              />
            </div>
          </div>
        )}
      </div>

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-info text-white no-print">
                <h5 className="modal-title">Appointment Details</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedAppointment(null)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <ul className="info-details">
                  <li>
                    <div className="details-header">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <span className="title">
                            #{selectedAppointment.appointmentId}
                          </span>
                          <span className="text">
                            {selectedAppointment.appointmentDate}{" "}
                            {selectedAppointment.appointmentTime}
                          </span>
                        </div>
                        <div className="col-md-6 text-right text-md-end">
                          <span
                            className={`badge badge-pill p-2 ${
                              selectedAppointment.status === "Confirmed"
                                ? "badge-success"
                                : selectedAppointment.status === "Canceled"
                                  ? "badge-danger"
                                  : "badge-warning"
                            }`}
                          >
                            {selectedAppointment.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <span className="title">Appointment Booked By:</span>{" "}
                    <span className="text">
                      {selectedAppointment.patient?.firstName ||
                        selectedAppointment.patient?.lastName}
                    </span>
                  </li>
                  <li>
                    <span className="title">Patient Name:</span>{" "}
                    <span className="text">
                      {selectedAppointment.patient_name ||
                        selectedAppointment.patient?.firstName}
                    </span>
                  </li>
                  <li>
                    <span className="title">Email:</span>{" "}
                    <span className="text">
                      {selectedAppointment.patient?.email || "N/A"}
                    </span>
                  </li>
                  <li>
                    <span className="title">Phone:</span>{" "}
                    <span className="text">
                      {selectedAppointment.patient?.phoneNumber || "N/A"}
                    </span>
                  </li>
                  <li>
                    <span className="title">Type:</span>{" "}
                    <span className="text">{selectedAppointment.type}</span>
                  </li>
                  {/* <li>
                    <span className="title">Paid Amount:</span>{" "}
                    <span className="text">
                     {selectedAppointment.paidAmount ? formatCurrency(selectedAppointment.paidAmount, selectedCurrency) : "N/A"}
                    </span>
                  </li> */}
                  <li>
                    <span className="title">Paid Amount:</span>{" "}
                    <span className="text">
                      {selectedAppointment.paidAmount &&
                      selectedAppointment.paidAmount !== "0"
                        ? formatCurrency(
                            selectedAppointment.paidAmount,
                            selectedCurrency,
                          )
                        : "Free / N/A"}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      <MessageModal
        show={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        title="Access Restricted"
        variant="warning"
        showConfirm={true}
        confirmText={
          <span>
            <i className="fas fa-crown me-2"></i> Subscribe Now
          </span>
        }
        onConfirm={() => {
          setShowAddonModal(false);
          history.push("/doctor/plan-selection");
        }}
        message={
          <div
            className="alert alert-warning border-0 p-0 mb-0"
            style={{ background: "transparent" }}
          >
            <div className="d-flex align-items-center">
              <i
                className="fas fa-calendar-check text-warning me-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <div className="text-start text-dark">
                <strong className="h5 d-block mb-1">
                  Self Appointment Addon Required!
                </strong>
                <p className="mb-0 small text-muted">
                  You have not subscribed to the Self Appointment Addon. Please
                  select a plan to enable patient registration and manual
                  booking features.
                </p>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

// Redux Mapping
const mapStateToProps = (state) => ({
  appointments: state.appointment.all || [],
  profiles: state.patientProfile.profiles || [],
  templateSlots: state.schedule.templateSlots || [], // Added
  daysOff: state.schedule.daysOff || [], // Added
  blockers: state.schedule.blockers || [],
  selectedCurrency: state.currencyReducer.selectedCurrency,
});

export default connect(mapStateToProps, {
  getAllAppointments,
  getAllPatientProfiles,
  updateAppointment,
  getScheduleData, // Added
  createAppointment, // Added
  setPatientProfile, // Added
})(Appointments);
